// ============= Imports =============
// External dependencies
import { useState, useCallback } from 'react';

// Internal dependencies - Types
import { FormState, TagOption, Suggestion } from '../types';

// ============= Types =============
/**
 * Interface defining the form state hook return values and methods
 * @interface FormStateHook
 */
interface FormStateHook {
  formData: FormState;
  paUserDetails: TagOption[];
  tagPickerSuggestions: Suggestion[];
  previousPAValues: string[];
  pendingRemovals: string[];
  handleTagsChange: (newTags: TagOption[]) => void;
  handleFloorChange: (newFloor: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormState>>;
  setPaUserDetails: React.Dispatch<React.SetStateAction<TagOption[]>>;
  setTagPickerSuggestions: React.Dispatch<React.SetStateAction<Suggestion[]>>;
  setPendingRemovals: React.Dispatch<React.SetStateAction<string[]>>;
  resetFormState: () => void;
}

// ============= Constants =============
/**
 * Initial state for the form
 */
const INITIAL_FORM_STATE: FormState = {
  twlegalPA: [],
  floorRoom: '',
};

// ============= Hook Implementation =============
/**
 * Custom hook for managing form state and related operations
 * Handles PA assignments, floor changes, and form reset functionality
 *
 * @returns {FormStateHook} Object containing form state and handlers
 */
export const useFormState = (): FormStateHook => {
  // ============= State Definitions =============
  /**
   * Main form state containing PA and floor data
   */
  const [formData, setFormData] = useState<FormState>(INITIAL_FORM_STATE);

  /**
   * State for storing PA user details as tag options
   */
  const [paUserDetails, setPaUserDetails] = useState<TagOption[]>([]);

  /**
   * State for tracking previous PA values
   */
  const [previousPAValues, setPreviousPAValues] = useState<string[]>([]);

  /**
   * State for managing pending PA removals
   */
  const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

  /**
   * State for tag picker suggestions
   */
  const [tagPickerSuggestions, setTagPickerSuggestions] = useState<
    Suggestion[]
    >([]);

  // ============= Handlers =============
  /**
   * Handles changes to PA tags
   * Updates form state and manages pending removals
   *
   * @param newTags - Array of new tag options
   */
  const handleTagsChange = useCallback(
    (newTags: TagOption[]) => {
      console.log('handleTagsChange called with:', newTags);

      // Store current values for comparison
      const currentValues = formData.twlegalPA;
      const newPAValues = newTags.map((tag) => tag.email || '');

      // Calculate removals
      const removedPAs = currentValues.filter(
        (pa) => !newPAValues.includes(pa)
      );

      if (removedPAs.length > 0) {
        setPendingRemovals(removedPAs);
      }

      if (newTags.length === 0) {
        console.log('Clearing all PA values');
        setPaUserDetails([]);
        setFormData((prev) => ({
          ...prev,
          twlegalPA: [],
        }));
        return;
      }

      setPaUserDetails(newTags);
      setFormData((prev) => ({
        ...prev,
        twlegalPA: newPAValues,
      }));

      console.log(
        'Tags changed:',
        newTags,
        'New PA Values:',
        newPAValues,
        'Removals:',
        removedPAs
      );
    },
    [formData.twlegalPA]
  );

  /**
   * Handles changes to floor selection
   *
   * @param newFloor - New floor value
   */
  const handleFloorChange = useCallback((newFloor: string) => {
    console.log('handleFloorChange called with:', newFloor);

    setFormData((prev) => ({
      ...prev,
      floorRoom: newFloor,
    }));
  }, []);

  /**
   * Resets all form state to initial values
   */
  const resetFormState = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setPaUserDetails([]);
    setTagPickerSuggestions([]);
    setPreviousPAValues([]);
  }, []);

  // ============= Hook Return =============
  return {
    formData,
    paUserDetails,
    tagPickerSuggestions,
    previousPAValues,
    pendingRemovals,
    setPendingRemovals,
    handleTagsChange,
    handleFloorChange,
    setFormData,
    setPaUserDetails,
    setTagPickerSuggestions,
    resetFormState,
  };
};
